/**
 * Sentry error helpers. Can be imported in any component.
 */
import * as Sentry from "@sentry/vue";

/**
 * Configures the Sentry error structure
 * @param  {[string]} componentName - component where the error is triggered from
 * @param  {[string]} method - method/function where the error is triggered from
 * @param  {[string]} errorMessage - the triggered error
 * @param  {[Record<string, unknown>]} context - optional key/value pairs attached as extra context
 */
export const configSentryError = (
    componentName: string,
    method: string,
    errorMessage: string,
    context?: Record<string, unknown>,
) => {
    const transactionName = componentName + " | " + method;
    Sentry.withScope(function (scope) {
        scope.setTransactionName(transactionName);
        if (context) {
            scope.setContext("details", context);
        }
        Sentry.captureException(new Error(errorMessage));
    });
};

export const setSentryUser = (emailAddress: string, sessionID: string) => {
    /**
     * Sets the user's identity
     * @param  {[string]} emailAddress
     * @param  {[string]} sessionID - ties the user to a session
     * @return {[void]} void
     */
    Sentry.setUser({
        id: sessionID,
        email: emailAddress,
    });
};

/**
 * Configures the Sentry error structure
 * @param  {[string]} transactionName - component/method where the error is triggered from
 * @param  {[string]} errorMessage - the triggered error
 */
export const configSentryApiError = (
    transactionName: string,
    errorMessage: string,
) => {
    Sentry.withScope(function (scope) {
        scope.setTransactionName(transactionName);
        Sentry.captureException(new Error(errorMessage));
    });
};

/**
 * Manually adds a breadcrumb in the Sentry event.
 * Breadcrumb config - https://develop.sentry.dev/sdk/event-payloads/breadcrumbs/
 * @param  {[string]} category - "started", "sentry.transaction", "ui.click", "click", "navigation", "error", "xhr"
 * @param  {[string]} message - the breadcrumb message
 * @param  {[Sentry.SeverityLevel]} level - "fatal", "critical", "error", "warning", "log", "info", and "debug".
 */
export const createSentryBreadcrumb = (
    category: string,
    message: string,
    level: Sentry.SeverityLevel,
) => {
    Sentry.addBreadcrumb({
        category: category,
        message: message,
        level: level,
    });
};

/**
 * Captures a warning-level Sentry message, with optional extra context.
 * @param  {[string]} message - the warning message
 * @param  {[Record<string, unknown>]} context - optional key/value pairs attached as extra context
 */
export const captureSentryWarning = (
    message: string,
    context?: Record<string, unknown>,
) => {
    Sentry.withScope(function (scope) {
        scope.setLevel("warning");
        if (context) {
            scope.setContext("details", context);
        }
        Sentry.captureMessage(message, "warning");
    });
};
